import { useEffect, useState } from "react";
import { TableData } from "@/App/ReconciliationFinancialTransactionsView/Table";
import { getShowFinancialReconciliationTransaction } from "@/Api/reconcialiationFinancialTransactions";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";

import { CircleDollarSign, ArrowLeft, ArrowDownCircle, ArrowUpCircle, AlertTriangle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AddReconciliationFileBtn } from "@/App/ReconciliationFinancialTransactionsView/Add";
import { FiltersView } from "@/App/ReconciliationFinancialTransactionsView/Filters";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnmatchedEntries } from "@/App/ReconciliationFinancialTransactionsView/UnmatchedEntries";
import { JobsPanel } from "@/App/ReconciliationFinancialTransactionsView/JobsPanel";

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const UNMATCHED_TAB = "unmatched";

export const PageView = () => {
  const [reconciliation, setReconciliation] = useState<any>({});
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState<Record<string, any>>({ payment_form_ids: "3,11", per_page: "100" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [activeType, setActiveType] = useState("2");
  const [unmatchedRefresh, setUnmatchedRefresh] = useState(0);
  const { uuid } = useParams();
  const navigate = useNavigate();

  const fetchData = async (
    page: number = 1,
    currentFilters = filters,
    type: string = activeType
  ) => {
    if (type === UNMATCHED_TAB) {
      setUnmatchedRefresh((prev) => prev + 1);
      return;
    }
    setState("loading");
    try {
      const params: Record<string, string> = {
        ...currentFilters,
        transaction_type_id: type,
        page: page.toString(),
        per_page: currentFilters.per_page || "100",
      };

      const { data } = await getShowFinancialReconciliationTransaction(
        uuid || "",
        params
      );

      setReconciliation(data?.response);
      setPagination(data?.response?.pagination || null);
      setCurrentPage(page);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setState("production");
    }
  };

  const handleFilter = async (filterData: any) => {
    const payload: Record<string, any> = {
      start_date: filterData?.start_date || "",
      end_date: filterData?.end_date || "",
      financial_transaction_type_id:
        filterData?.financial_transaction_type_id || "",
      reconciliation_status_id: filterData?.reconciliation_status_id || "",
      per_page: filterData?.per_page || "100",
    };

    if (filterData?.payment_form_ids && filterData.payment_form_ids.length > 0) {
      payload.payment_form_ids = filterData.payment_form_ids.join(",");
    }

    setFilters(payload);
    await fetchData(1, payload, activeType);
  };

  const handleTypeChange = (type: string) => {
    setActiveType(type);
    fetchData(1, filters, type);
  };

  const handlePageChange = (page: number) => {
    fetchData(page, filters, activeType);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isMixed = !!reconciliation?.id && !reconciliation?.financial_type_id;

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/reconciliacao-financeira")}
            title="Voltar para listagem"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            <CircleDollarSign className="h-6 w-6" />
            Conciliação Financeira
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <AddReconciliationFileBtn
            id={reconciliation?.id?.toString() || ""}
            itsPaidPayabled={reconciliation?.financial_type_id == 2}
            updateList={() => fetchData(currentPage, filters, activeType)}
          />
        </div>
      </div>

      <FiltersView onFilter={handleFilter} noFormName />

      {reconciliation?.id && (
        <JobsPanel
          processId={reconciliation.id}
          onJobsComplete={() => fetchData(currentPage, filters, activeType)}
        />
      )}

      {isMixed && (
        <Tabs value={activeType} onValueChange={handleTypeChange} className="mb-3">
          <TabsList className="h-14 w-full md:w-auto gap-2 p-1.5 bg-white dark:bg-gray-800 border">
            <TabsTrigger
              value="2"
              className="h-full flex-1 md:flex-none px-8 text-base font-semibold gap-2 text-green-700 data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <ArrowDownCircle className="h-5 w-5" />
              Contas a Receber
            </TabsTrigger>
            <TabsTrigger
              value="1"
              className="h-full flex-1 md:flex-none px-8 text-base font-semibold gap-2 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <ArrowUpCircle className="h-5 w-5" />
              Contas a Pagar
            </TabsTrigger>
            <TabsTrigger
              value={UNMATCHED_TAB}
              className="h-full flex-1 md:flex-none px-8 text-base font-semibold gap-2 text-amber-700 data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <AlertTriangle className="h-5 w-5" />
              Divergentes
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isMixed && activeType === UNMATCHED_TAB ? (
        <UnmatchedEntries
          processUuid={uuid || ""}
          refreshKey={unmatchedRefresh}
          onLaunched={() => fetchData(1, filters, "2")}
        />
      ) : state === "loading" ? (
        <CreateLoaderTable cols={6} lines={6} />
      ) : (
        <TableData
          dataApi={reconciliation}
          refetch={() => fetchData(currentPage, filters, activeType)}
          pagination={pagination}
          onPageChange={handlePageChange}
          filters={filters}
        />
      )}
    </Main>
  );
};
